import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  ServerIcon,
  CogIcon,
  TagIcon,
  UserGroupIcon,
  CreditCardIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  PlayIcon,
  StopIcon,
  ArrowTopRightOnSquareIcon,
  ShoppingCartIcon,
  SparklesIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import {
  ServerIcon as ServerIconSolid,
  BuildingOfficeIcon as BuildingOfficeIconSolid,
  TagIcon as TagIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import Card from '../ui/Card';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import logger from '../../utils/logger';

const ERPDashboard = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [instances, setInstances] = useState([]);
  const [licensedModules, setLicensedModules] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [profileRes, modulesRes, instancesRes] = await Promise.all([
        axiosInstance.get('/api/business/profile').catch(() => ({ data: { success: false } })),
        axiosInstance.get('/api/business/modules/license').catch(() => ({ data: { success: false } })),
        axiosInstance.get('/api/erp/instances').catch(() => ({ data: { success: false } })),
      ]);

      if (profileRes.data.success) {
        setBusinessProfile(profileRes.data.data);
      }

      if (modulesRes.data.success) {
        setLicensedModules(modulesRes.data.data.licensed || []);
      }

      if (instancesRes.data.success) {
        setInstances(instancesRes.data.data || []);
      }
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30';
      case 'stopped': return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30';
      case 'maintenance': return 'text-yellow-700 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/30';
      default: return 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-800';
    }
  };

  const getEnvironmentColor = (environment) => {
    switch (environment) {
      case 'production': return 'from-red-500 to-red-600';
      case 'staging': return 'from-yellow-500 to-yellow-600';
      case 'development': return 'from-green-500 to-green-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // Navigate to sibling routes (relative to admin layout root)
  const goTo = (path) => navigate(`/${path}`);

  if (loading) {
    return (
      <div className="h-full bg-gray-50 dark:bg-[#111111] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const runningCount = instances.filter(i => i.status === 'running').length;
  const stoppedCount = instances.filter(i => i.status === 'stopped').length;

  return (
    <div className="h-full bg-gray-50 dark:bg-[#111111] flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-2">
        <div className="flex items-center gap-2">
          <BuildingOfficeIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
          <h1 className="text-sm font-semibold text-gray-900 dark:text-white">ERP Dashboard</h1>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {instances.length} instance{instances.length !== 1 ? 's' : ''} · {licensedModules.length} module{licensedModules.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* ERP Instances */}
          <button
            onClick={() => goTo('erp-instances')}
            className="text-left bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-700 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <ServerIconSolid className="w-4.5 h-4.5 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{instances.length}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">ERP Instances</div>
                </div>
              </div>
              <div className="text-right">
                {runningCount > 0 && (
                  <div className="text-xs font-medium text-green-600 dark:text-green-400">{runningCount} running</div>
                )}
                {stoppedCount > 0 && (
                  <div className="text-xs text-gray-500">{stoppedCount} stopped</div>
                )}
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 mt-1 ml-auto transition-colors" />
              </div>
            </div>
          </button>

          {/* Licensed Modules */}
          <button
            onClick={() => goTo('erp-catalog')}
            className="text-left bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-purple-300 dark:hover:border-purple-700 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <TagIconSolid className="w-4.5 h-4.5 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{licensedModules.length}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Licensed Modules</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs text-orange-600 dark:text-orange-400 font-medium group-hover:underline">Browse</span>
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-purple-500 mt-1 ml-auto transition-colors" />
              </div>
            </div>
          </button>

          {/* Business Tier */}
          <button
            onClick={() => goTo('billing')}
            className="text-left bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-green-300 dark:hover:border-green-700 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <BuildingOfficeIconSolid className="w-4.5 h-4.5 text-white" />
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">Professional</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Subscription Tier</div>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs text-orange-600 dark:text-orange-400 font-medium group-hover:underline">Manage</span>
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-green-500 mt-1 ml-auto transition-colors" />
              </div>
            </div>
          </button>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <button
              onClick={() => goTo('erp-instances')}
              className="flex items-center space-x-2.5 p-2.5 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/10 dark:hover:bg-orange-900/20 rounded-lg transition-colors text-left"
            >
              <PlusIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 shrink-0" />
              <span className="text-xs font-medium text-gray-900 dark:text-white">New Instance</span>
            </button>

            <button
              onClick={() => goTo('erp-catalog')}
              className="flex items-center space-x-2.5 p-2.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/10 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-left"
            >
              <ShoppingCartIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-xs font-medium text-gray-900 dark:text-white">Module Catalog</span>
            </button>

            <button
              onClick={() => goTo('erp-users')}
              className="flex items-center space-x-2.5 p-2.5 bg-green-50 hover:bg-green-100 dark:bg-green-900/10 dark:hover:bg-green-900/20 rounded-lg transition-colors text-left"
            >
              <UserGroupIcon className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
              <span className="text-xs font-medium text-gray-900 dark:text-white">Manage Users</span>
            </button>

            {/* AI Customizer button hidden */}

            <button
              onClick={() => goTo('billing')}
              className="flex items-center space-x-2.5 p-2.5 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800 rounded-lg transition-colors text-left"
            >
              <CreditCardIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
              <span className="text-xs font-medium text-gray-900 dark:text-white">Billing</span>
            </button>
          </div>
        </div>

        {/* Instances & Modules side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* ERP Instances */}
          <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">ERP Instances</h3>
              <button
                onClick={() => goTo('erp-instances')}
                className="text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
              >
                View All
              </button>
            </div>

            {instances.length === 0 ? (
              <div className="text-center py-10 px-4">
                <ServerIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No ERP instances yet</p>
                <button
                  onClick={() => goTo('erp-instances')}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
                  Create First Instance
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {instances.slice(0, 5).map((instance) => (
                  <button
                    key={instance._id || instance.id}
                    onClick={() => goTo('erp-instances')}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className={`w-7 h-7 shrink-0 bg-gradient-to-br ${getEnvironmentColor(instance.environment)} rounded flex items-center justify-center`}>
                        <ServerIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {instance.displayName || instance.subdomain}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{instance.environment}</div>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${getStatusColor(instance.status)}`}>
                      {instance.status}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Active Modules */}
          <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active Modules</h3>
              <button
                onClick={() => goTo('erp-catalog')}
                className="text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
              >
                Manage
              </button>
            </div>

            {licensedModules.length === 0 ? (
              <div className="text-center py-10 px-4">
                <RectangleStackIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No modules activated</p>
                <button
                  onClick={() => goTo('erp-catalog')}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
                  Browse Modules
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {licensedModules.map((moduleId) => (
                  <div
                    key={moduleId}
                    className="flex items-center justify-between px-4 py-2.5"
                  >
                    <div className="flex items-center space-x-2.5">
                      <CheckCircleIconSolid className="w-4 h-4 text-green-500 shrink-0" />
                      <span className="text-sm text-gray-900 dark:text-white capitalize">{moduleId.replace(/_/g, ' ')}</span>
                    </div>
                    <span className="text-[10px] font-medium text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30 px-1.5 py-0.5 rounded">
                      Active
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERPDashboard;
